import { faInfoCircle, faLink, faUnlink } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Modal } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import useCommon from "hooks/useCommon"
import { useMemo, useState } from "react"
import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"
import Select from "react-select"
import {
  CONTRACT_TYPE_LABELS,
  Contract as BackendContract,
  ContractType,
  useCreateContractAssetMutation,
  useListContractsQuery,
  useListCountriesQuery,
} from "services/aimsBackend"

type Option = { value: string; label: string }

export default function AssetRegistrationTab({ assetId, assetIso3 }: { assetId: number; assetIso3: string }) {
  const { showSnackBar } = useCommon()

  const { data: contractsList, isFetching } = useListContractsQuery()
  const { data: countries = [] } = useListCountriesQuery()
  const [createContractAsset, { isLoading: isLinking }] = useCreateContractAssetMutation()

  const contracts = useMemo(() => contractsList?.results ?? [], [contractsList])
  const countryNameByIso3 = useMemo(() => new Map(countries.map(c => [c.iso3, c.name])), [countries])

  const [showLinkModal, setShowLinkModal] = useState(false)
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null)
  const [selectedContractType, setSelectedContractType] = useState<Option | null>(null)

  // Modal filters
  const [filterYears, setFilterYears] = useState<Option[]>([])
  const [filterOwner, setFilterOwner] = useState<Option | null>(null)
  const [filterType, setFilterType] = useState<Option | null>(null)

  const linkedContracts = useMemo(() => contracts.filter(c => (c.assets ?? []).includes(assetId)), [contracts, assetId])

  // Derive filter options from the actual contracts data
  const yearOptions = useMemo<Option[]>(() => {
    const set = new Set<number>()
    contracts.forEach(c => set.add(c.subscription_year))
    return Array.from(set)
      .sort((a, b) => b - a)
      .map(y => ({ value: String(y), label: String(y) }))
  }, [contracts])

  const ownerOptions = useMemo<Option[]>(() => {
    const set = new Set<string>()
    contracts.forEach(c => c.owner && set.add(c.owner))
    return Array.from(set)
      .sort()
      .map(o => ({ value: o, label: o }))
  }, [contracts])

  const typeOptions = useMemo<Option[]>(
    () =>
      Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
    [],
  )

  const availableContracts = useMemo(() => {
    let filtered = contracts.filter(c => !!assetIso3 && c.countries.includes(assetIso3))
    if (filterYears.length > 0) {
      const years = filterYears.map(y => Number(y.value))
      filtered = filtered.filter(c => years.includes(c.subscription_year))
    }
    if (filterOwner) {
      filtered = filtered.filter(c => c.owner === filterOwner.value)
    }
    if (filterType) {
      filtered = filtered.filter(c => c.contract_types.includes(filterType.value as ContractType))
    }
    return filtered
  }, [assetIso3, contracts, filterYears, filterOwner, filterType])

  const contractSelectOptions = useMemo<Array<{ value: number; label: string }>>(
    () =>
      availableContracts.map(c => ({
        value: c.id,
        label: `${c.name} (${c.contract_types.map(t => CONTRACT_TYPE_LABELS[t] ?? t).join(", ")})`,
      })),
    [availableContracts],
  )

  // Contract types selectable for the attach are restricted to the chosen contract's types
  const selectedContract = useMemo(
    () => contracts.find(c => c.id === selectedContractId) ?? null,
    [contracts, selectedContractId],
  )

  const attachTypeOptions = useMemo<Option[]>(() => {
    if (!selectedContract) return []
    return selectedContract.contract_types.map(t => ({
      value: t,
      label: CONTRACT_TYPE_LABELS[t] ?? t,
    }))
  }, [selectedContract])

  const resetFilters = () => {
    setFilterYears([])
    setFilterOwner(null)
    setFilterType(null)
    setSelectedContractId(null)
    setSelectedContractType(null)
  }

  const handleLink = async () => {
    if (!selectedContract || !selectedContractType) return

    try {
      await createContractAsset({
        contract: selectedContract.id,
        asset: assetId,
        contract_type: selectedContractType.value as ContractType,
      }).unwrap()
      showSnackBar("Asset linked to contract successfully", "success")
      setShowLinkModal(false)
      resetFilters()
    } catch (err: any) {
      const data = err?.data
      const message =
        typeof data === "string"
          ? data
          : data && typeof data === "object"
          ? Object.entries(data)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
              .join("; ")
          : "Failed to link contract"
      showSnackBar(message, "error")
    }
  }

  const handleUnlink = () => {
    showSnackBar("Unlinking is not supported by the backend yet.", "warning")
  }

  // Auto-fill the subscription year when a contract is picked
  const handlePickContract = (contractId: number | null) => {
    setSelectedContractId(contractId)
    setSelectedContractType(null)
  }

  const columns: Array<ColumnShape<BackendContract>> = [
    { key: "name", dataKey: "name", title: "Contract Name", width: 200, flexGrow: 1 },
    { key: "owner", dataKey: "owner", title: "Owner", width: 150, flexGrow: 1 },
    {
      key: "countries",
      title: "Countries",
      width: 150,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => rowData.countries.map(c => countryNameByIso3.get(c) ?? c).join(", "),
    },
    {
      key: "types",
      title: "Type",
      width: 160,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => rowData.contract_types.map(t => CONTRACT_TYPE_LABELS[t] ?? t).join(", "),
    },
    {
      key: "subscription_year",
      dataKey: "subscription_year",
      title: "Subscription Year",
      width: 140,
      flexGrow: 1,
    },
    {
      key: "rounds_covered",
      title: "Rounds Covered",
      width: 200,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => {
        const entries = Object.entries(rowData.rounds_covered ?? {})
        if (entries.length === 0) return "—"
        return entries
          .map(([type, rounds]) => `${CONTRACT_TYPE_LABELS[type as ContractType] ?? type}: ${rounds.length || 0}`)
          .join(", ")
      },
    },
    {
      key: "actions",
      title: "",
      width: 160,
      flexGrow: 0,
      cellRenderer: () => (
        <Button action={handleUnlink} label="Unlink" icon={faUnlink} className="btn-full danger action-btn" />
      ),
    },
  ]

  const linkModal = (
    <Modal
      open={showLinkModal}
      modalHeading="Link Asset to Contract"
      primaryButtonText={isLinking ? "Linking..." : "Link"}
      secondaryButtonText="Cancel"
      onRequestClose={() => setShowLinkModal(false)}
      onSecondarySubmit={() => setShowLinkModal(false)}
      onRequestSubmit={handleLink}
      primaryButtonDisabled={!selectedContractId || !selectedContractType || isLinking}
    >
      <div className="mb-3">
        <div className="mb-3 p-3" style={{ background: "#e5f6fd", borderRadius: 4 }}>
          <p className="m-0" style={{ fontSize: 13 }}>
            Only contracts that share this asset&apos;s country ({assetIso3 || "not set"}) can be assigned.
          </p>
        </div>
        <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
          Subscription Year
        </p>
        <Select
          classNamePrefix="wfp--react-select"
          className="wfp--react-select-container"
          options={yearOptions}
          isMulti
          value={filterYears}
          onChange={val => {
            setFilterYears(val as Option[])
            setSelectedContractId(null)
          }}
          placeholder="Filter by year"
          isClearable
        />
      </div>
      <div className="mb-3">
        <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
          Owner
        </p>
        <Select
          classNamePrefix="wfp--react-select"
          className="wfp--react-select-container"
          options={ownerOptions}
          value={filterOwner}
          onChange={val => {
            setFilterOwner(val)
            setSelectedContractId(null)
          }}
          placeholder="Filter by owner"
          isClearable
        />
      </div>
      <div className="mb-3">
        <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
          Contract Type
        </p>
        <Select
          classNamePrefix="wfp--react-select"
          className="wfp--react-select-container"
          options={typeOptions}
          value={filterType}
          onChange={val => {
            setFilterType(val)
            setSelectedContractId(null)
          }}
          placeholder="Filter by type"
          isClearable
        />
      </div>
      <div className="mb-3">
        <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
          Select Contract
        </p>
        <Select
          classNamePrefix="wfp--react-select"
          className="wfp--react-select-container"
          options={contractSelectOptions}
          value={contractSelectOptions.find(o => o.value === selectedContractId) || null}
          onChange={opt => handlePickContract(opt?.value ?? null)}
          placeholder="Search contracts..."
          isSearchable
          noOptionsMessage={() =>
            contracts.length === 0
              ? "No contracts available to link."
              : "No contracts share this asset's country and match the selected filters."
          }
        />
      </div>

      <div className="mb-3">
        <p className="m-0 mb-1" style={{ fontWeight: 600, fontSize: 13 }}>
          Attach as type
        </p>
        <Select
          classNamePrefix="wfp--react-select"
          className="wfp--react-select-container"
          options={attachTypeOptions}
          value={selectedContractType}
          onChange={val => setSelectedContractType(val)}
          placeholder={selectedContract ? "Pick the contract type for this link" : "Select a contract first"}
          isDisabled={!selectedContract}
        />
      </div>
    </Modal>
  )

  if (!isFetching && linkedContracts.length === 0) {
    return (
      <div className="mt-3">
        <div className="d-flex align-items-center mb-3 p-3" style={{ background: "#e5f6fd", borderRadius: 4, gap: 8 }}>
          <FontAwesomeIcon icon={faInfoCircle} style={{ color: "#007dbc" }} />
          <p className="m-0" style={{ fontSize: 13 }}>
            This asset is not linked to any contract. Link it to one or more contracts to enable ADA and LIA analysis
            rounds.
          </p>
        </div>

        <Button
          action={() => {
            resetFilters()
            setShowLinkModal(true)
          }}
          label="Link Contract"
          className="btn-full green"
          icon={faLink}
        />

        {linkModal}
      </div>
    )
  }

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-end mb-3">
        <Button
          action={() => {
            resetFilters()
            setShowLinkModal(true)
          }}
          label="Link Contract"
          className="btn-full green"
          icon={faLink}
        />
      </div>

      <div style={{ height: Math.max(linkedContracts.length * 45 + 50, 200), minHeight: 200 }}>
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={linkedContracts}
              width={width}
              height={height}
              rowKey="id"
              className="custom-table"
              rowHeight={40}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No contracts linked</p>}
            />
          )}
        </AutoResizer>
      </div>

      {linkModal}
    </div>
  )
}
